import { cn } from "@btnet/utilities-core";

import {
  type IBuiSectionHeaderProps,
  type IBuiSectionProps,
} from "./BuiSection.types";

/**
 * BuiSection - a presentational container primitive for grouping related content.
 *
 * Renders a padded surface. Compose with `BuiSection.Header` for a header row;
 * anything else is passed as children.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the container.
 */
const BuiSectionComponent = (props: IBuiSectionProps) => {
  const { className, "data-testid": testid, children } = props;

  return (
    <div
      className={cn("bui-section flex flex-col bg-gray-0 py-2 px-4", className)}
      data-testid={testid}
    >
      {children}
    </div>
  );
};

/**
 * BuiSection.Header - a header row for a `BuiSection`.
 *
 * Lays out its children in a flex row with space between them, suited for a title alongside
 * actions.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the header.
 */
const BuiSectionHeader = (props: IBuiSectionHeaderProps) => {
  const { className, "data-testid": testid, children } = props;

  return (
    <div
      className={cn(
        "flex items-center justify-between w-full gap-1",
        className,
      )}
      data-testid={testid}
    >
      {children}
    </div>
  );
};

/**
 * BuiSection - a presentational container primitive for grouping related content.
 *
 * Renders a padded surface. Compose with `BuiSection.Header` for a header row;
 * anything else is passed as children.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the container.
 */
export const BuiSection = Object.assign(BuiSectionComponent, {
  /**
   * BuiSection.Header - a header row for a `BuiSection`.
   *
   * Lays out its children in a flex row with space between them, suited for a title alongside
   * actions.
   * @param props - The component props.
   * @param props.className - Additional CSS class names.
   * @param props["data-testid"] - Test ID for the header.
   */
  Header: BuiSectionHeader,
});
